import { ListingType } from "@redotech/cockroachdb-codegen/tables/product/listing-type";
import { ShopifyVariantFieldType } from "@redotech/cockroachdb-codegen/tables/product/shopify-variant-field-type";
import { attributeDefinitionIdFormat } from "@redotech/redo-product-id";
import { ShopifyIdType } from "@redotech/shopify-util/id";
import { shopifyGidSchema } from "@redotech/shopify-util/id-schema";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  AttributeType,
  AttributeValueSchema,
  AttributeValueType,
  createSafeNullableEnumSchema,
  HsCodeSchema,
  MoneySchema,
} from "../attributes";
import { BaseProductListingSchema } from "../base-listing";
import {
  CountryCodeSchema,
  ShopifyWeightSchema,
  UnitPriceMeasurementMeasuredUnitSchema,
} from "./common-schemas";
import {
  ShopifyListingMediaSchema,
  ShopifyListingMediaWithoutRedoIds,
  ShopifyListingMediaWithShopifyIds,
} from "./shopify-media";
import { ShopifyMetafieldSchema } from "./shopify-metafields";

export enum ShopifyInventoryPolicyEnum {
  CONTINUE = "CONTINUE",
  DENY = "DENY",
}
export const ShopifyInventoryPolicySchema = createEnum(
  ShopifyInventoryPolicyEnum,
);

const ShopifyListingSelectedOptionSchema_ = z.object({
  name: z.string(),
  value: z.string(),
});
export const ShopifyListingSelectedOptionSchema: z.ZodType<ShopifyListingSelectedOption> =
  ShopifyListingSelectedOptionSchema_;
export interface ShopifyListingSelectedOption extends z.infer<
  typeof ShopifyListingSelectedOptionSchema_
> {}

const UnitPriceMeasurementSchema_ = z.object({
  measuredType: z.string().nullable(),
  quantityUnit: UnitPriceMeasurementMeasuredUnitSchema.nullable(),
  quantityValue: z.number(),
  referenceUnit: UnitPriceMeasurementMeasuredUnitSchema.nullable(),
  referenceValue: z.number(),
});
export const UnitPriceMeasurementSchema: z.ZodType<UnitPriceMeasurement> =
  UnitPriceMeasurementSchema_;
export interface UnitPriceMeasurement extends z.infer<
  typeof UnitPriceMeasurementSchema_
> {}

const InventoryItemSchema_ = z.object({
  countryCodeOfOrigin: CountryCodeSchema.nullable(),
  hsCode: HsCodeSchema.nullable(),
  weight: ShopifyWeightSchema.nullable(),
  requiresShipping: z.boolean().nullable(),
  tracked: z.boolean().nullable(),
});
export const InventoryItemSchema: z.ZodType<InventoryItem> =
  InventoryItemSchema_;
export interface InventoryItem extends z.infer<typeof InventoryItemSchema_> {}
type InventoryItemWithoutRedoIds = Omit<InventoryItem, "hsCode"> & {
  hsCode: { code: string } | null;
};

export const ShopifyVariantListingSchema_ = BaseProductListingSchema.extend({
  listingType: z.literal(ListingType.Shopify),
  id: z.bigint(),
  shopifyVariantId: shopifyGidSchema(ShopifyIdType.ProductVariant).nullable(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  inventoryPolicy: ShopifyInventoryPolicySchema,
  price: z.number(),
  compareAtPrice: z.number().nullable(),
  inventoryItem: InventoryItemSchema,
  unitPrice: MoneySchema.nullable(),
  showUnitPrice: z.boolean().nullable(),
  unitPriceMeasurement: UnitPriceMeasurementSchema.nullable(),
  selectedOptions: z.array(ShopifyListingSelectedOptionSchema),
  media: z.array(ShopifyListingMediaSchema),
  metafields: z.array(ShopifyMetafieldSchema),
  taxable: z.boolean().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ShopifyVariantListingSchema: z.ZodType<ShopifyVariantListing> =
  ShopifyVariantListingSchema_;
export interface ShopifyVariantListing extends z.infer<
  typeof ShopifyVariantListingSchema_
> {}

export type ShopifyVariantListingWithoutRedoIds = Omit<
  ShopifyVariantListing,
  "id" | "productId" | "media" | "inventoryItem"
> & {
  media: ShopifyListingMediaWithoutRedoIds[];
  inventoryItem: InventoryItemWithoutRedoIds;
};

export type ShopifyVariantListingWithShopifyVariantId = Omit<
  ShopifyVariantListing,
  "shopifyVariantId"
> & { shopifyVariantId: string };

export type ShopifyVariantListingWithShopifyIds = Omit<
  ShopifyVariantListing,
  "shopifyVariantId" | "media"
> & { shopifyVariantId: string; media: ShopifyListingMediaWithShopifyIds[] };

export const ShopifyVariantFieldTypeSchema = createEnum(
  ShopifyVariantFieldType,
);

const ShopifyVariantMappingSchema_ = z.object({
  fieldName: ShopifyVariantFieldTypeSchema,
  attributeDefinitionId: attributeDefinitionIdFormat.schema,
});
export const ShopifyVariantMappingSchema: z.ZodType<ShopifyVariantMapping> =
  ShopifyVariantMappingSchema_;
export interface ShopifyVariantMapping extends z.infer<
  typeof ShopifyVariantMappingSchema_
> {}

export const ShopifyVariantAttributeType = {
  [ShopifyVariantFieldType.Sku]: AttributeType.ShortText,
  [ShopifyVariantFieldType.RequiresShipping]: AttributeType.Boolean,
  [ShopifyVariantFieldType.CountryOfOrigin]: AttributeType.Enum,
  [ShopifyVariantFieldType.HsCode]: AttributeType.HsCode,
  [ShopifyVariantFieldType.Weight]: AttributeType.Weight,
  [ShopifyVariantFieldType.Barcode]: AttributeType.ShortText,
  [ShopifyVariantFieldType.Price]: AttributeType.Money,
  [ShopifyVariantFieldType.Cost]: AttributeType.Money,
  [ShopifyVariantFieldType.CompareAtPrice]: AttributeType.Money,
  [ShopifyVariantFieldType.Taxable]: AttributeType.Boolean,
  [ShopifyVariantFieldType.InventoryPolicy]: AttributeType.Enum,
  [ShopifyVariantFieldType.ShowUnitPrice]: AttributeType.Boolean,
  [ShopifyVariantFieldType.UpmMeasuredType]: AttributeType.ShortText,
  [ShopifyVariantFieldType.UpmQuantityUnit]: AttributeType.Enum,
  [ShopifyVariantFieldType.UpmQuantityValue]: AttributeType.Float,
  [ShopifyVariantFieldType.UpmReferenceUnit]: AttributeType.Enum,
  [ShopifyVariantFieldType.UpmReferenceValue]: AttributeType.Float,
  [ShopifyVariantFieldType.DisplayName]: AttributeType.ShortText,
  [ShopifyVariantFieldType.Title]: AttributeType.ShortText,
  [ShopifyVariantFieldType.Metafield]: AttributeType.Json,
  [ShopifyVariantFieldType.InventoryTracked]: AttributeType.Boolean,
} satisfies Record<ShopifyVariantFieldType, AttributeType>;

export const ShopifyVariantAttributeSchema = {
  [ShopifyVariantFieldType.Sku]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Sku]
    ].nullable(),
  [ShopifyVariantFieldType.RequiresShipping]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.RequiresShipping]
    ].nullable(),
  [ShopifyVariantFieldType.CountryOfOrigin]:
    createSafeNullableEnumSchema(CountryCodeSchema),
  [ShopifyVariantFieldType.HsCode]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.HsCode]
    ].nullable(),
  [ShopifyVariantFieldType.Weight]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Weight]
    ].nullable(),
  [ShopifyVariantFieldType.Barcode]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Barcode]
    ].nullable(),
  [ShopifyVariantFieldType.Price]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Price]
    ].nullable(),
  [ShopifyVariantFieldType.Cost]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Cost]
    ].nullable(),
  [ShopifyVariantFieldType.CompareAtPrice]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.CompareAtPrice]
    ].nullable(),
  [ShopifyVariantFieldType.Taxable]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Taxable]
    ].nullable(),
  [ShopifyVariantFieldType.InventoryPolicy]: createSafeNullableEnumSchema(
    ShopifyInventoryPolicySchema,
  ),
  [ShopifyVariantFieldType.ShowUnitPrice]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.ShowUnitPrice]
    ].nullable(),
  [ShopifyVariantFieldType.UpmMeasuredType]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.UpmMeasuredType]
    ].nullable(),
  [ShopifyVariantFieldType.UpmQuantityUnit]: createSafeNullableEnumSchema(
    UnitPriceMeasurementMeasuredUnitSchema,
  ),
  [ShopifyVariantFieldType.UpmQuantityValue]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.UpmQuantityValue]
    ].nullable(),
  [ShopifyVariantFieldType.UpmReferenceUnit]: createSafeNullableEnumSchema(
    UnitPriceMeasurementMeasuredUnitSchema,
  ),
  [ShopifyVariantFieldType.UpmReferenceValue]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.UpmReferenceValue]
    ].nullable(),
  [ShopifyVariantFieldType.DisplayName]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.DisplayName]
    ].nullable(), // Soon to be deleted
  [ShopifyVariantFieldType.Title]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Title]
    ].nullable(), // Soon to be deleted
  [ShopifyVariantFieldType.Metafield]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.Metafield]
    ].nullable(),
  [ShopifyVariantFieldType.InventoryTracked]:
    AttributeValueSchema[
      ShopifyVariantAttributeType[ShopifyVariantFieldType.InventoryTracked]
    ].nullable(),
};

export type ShopifyVariantAttributeValueType = {
  [key in ShopifyVariantFieldType]: (typeof ShopifyVariantAttributeSchema)[key] extends z.ZodType<
    infer T
  >
    ? T
    : never;
};

export function getShopifyVariantAttributeValue<
  T extends ShopifyVariantFieldType,
>(field: T, value: unknown): ShopifyVariantAttributeValueType[T] | null {
  return ShopifyVariantAttributeSchema[field].parse(
    value,
  ) as ShopifyVariantAttributeValueType[T];
}

export function getShopifyVariantAttributeType<
  T extends ShopifyVariantFieldType,
>(field: T): (typeof ShopifyVariantAttributeType)[T] {
  return ShopifyVariantAttributeType[field];
}

export function getShopifyVariantListingAttributeValue<
  T extends ShopifyVariantFieldType,
>({
  listing,
  field,
}: {
  listing: ShopifyVariantListing;
  field: T;
}): AttributeValueType[AttributeType] | null {
  let fieldValue;
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (field) {
    case "sku":
      fieldValue = listing.sku;
      break;
    case "barcode":
      fieldValue = listing.barcode;
      break;
    case "requires_shipping":
      fieldValue = listing.inventoryItem.requiresShipping;
      break;
    case "country_of_origin":
      fieldValue = listing.inventoryItem.countryCodeOfOrigin;
      break;
    case "hs_code":
      fieldValue = listing.inventoryItem.hsCode;
      break;
    case "weight":
      fieldValue = listing.inventoryItem.weight;
      break;
    case "price":
      fieldValue = listing.price;
      break;
    case "cost":
      fieldValue = listing.unitPrice;
      break;
    case "compare_at_price":
      fieldValue = listing.compareAtPrice;
      break;
    case "taxable":
      fieldValue = listing.taxable;
      break;
    case "inventory_policy":
      fieldValue = listing.inventoryPolicy;
      break;
    case "show_unit_price":
      fieldValue = listing.showUnitPrice;
      break;
    case "upm_measured_type":
      fieldValue = listing.unitPriceMeasurement?.measuredType ?? null;
      break;
    case "upm_quantity_unit":
      fieldValue = listing.unitPriceMeasurement?.quantityUnit ?? null;
      break;
    case "upm_quantity_value":
      fieldValue = listing.unitPriceMeasurement?.quantityValue ?? null;
      break;
    case "upm_reference_unit":
      fieldValue = listing.unitPriceMeasurement?.referenceUnit ?? null;
      break;
    case "upm_reference_value":
      fieldValue = listing.unitPriceMeasurement?.referenceValue ?? null;
      break;
    case "display_name":
      fieldValue = null;
      break;
    case "title":
      fieldValue = null;
      break;
    case "metafield":
      fieldValue = null;
      break;
    case "inventory_tracked":
      fieldValue = listing.inventoryItem.tracked;
      break;
    default:
      assertNever(field);
  }

  return ShopifyVariantAttributeSchema[field].parse(fieldValue);
}
